import { FuseConfirmationConfig } from "@fuse/services/confirmation";
import { FILE_TYPES, ISociuuFields, MAX_FILE_SIZE_BY_TYPE } from "./users-import.interface";

const MAX_COLUMNS: number = 50;
const MAX_ROWS: number = 5_000;
const MAXIMUM_ROWS: string = '5,000';

export const maxFileSizesBytes: MAX_FILE_SIZE_BY_TYPE = {
    [FILE_TYPES.xlsx]: 5e6, //5MB
    [FILE_TYPES.csv]: 5e6, //5MB
    [FILE_TYPES.xls]: 5e6, //5MB
};

export const maxFileSizesMB: MAX_FILE_SIZE_BY_TYPE = {
    [FILE_TYPES.xlsx]: 100, //100MB
    [FILE_TYPES.csv]: 5, //5MB
    [FILE_TYPES.xls]: 5, //5MB
};

enum STEPPER_SETTINGS {
  STEP_ONE = 1,
  STEP_TWO = 2,
  STEP_THREE = 3,
  STEP_FOUR = 4,
  STEP_FIVE = 5,
  STEP_SIX = 6,
}

enum SOCIUU_FIELDS_KEYS {
  EMAIL = 'email',
  DEPT = 'department',
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  FULL_NAME = 'full_name',
  PHONE = 'phone',
  TITLE_1 = 'title_1',
  TITLE_2 = 'title_2',
  PHONE_COUNTRY_CODE = 'phone_country_code',
  USERGROUP = 'usergroup',
};

const SOCIUU_FIELDS: Array<ISociuuFields> = [
  {
    key: SOCIUU_FIELDS_KEYS.TITLE_1,
    value: 'Title 1'
  },
  {
    key: SOCIUU_FIELDS_KEYS.TITLE_2,
    value: 'Title 2'
  },
  {
    key: SOCIUU_FIELDS_KEYS.FIRST_NAME,
    value: 'First Name'
  },
  {
    key: SOCIUU_FIELDS_KEYS.LAST_NAME,
    value: 'Last Name'
  },
  {
    key: SOCIUU_FIELDS_KEYS.FULL_NAME,
    value: 'Full Name'
  },
  {
    key: SOCIUU_FIELDS_KEYS.EMAIL,
    value: 'Email'
  },
  {
    key: SOCIUU_FIELDS_KEYS.PHONE_COUNTRY_CODE,
    value: 'Phone Country Code'
  },
  {
    key: SOCIUU_FIELDS_KEYS.PHONE,
    value: 'Phone'
  },
  {
    key: SOCIUU_FIELDS_KEYS.DEPT,
    value: 'Department'
  },
  {
    key: SOCIUU_FIELDS_KEYS.USERGROUP,
    value: 'Usergroup'
  },
];

const colorMapping: { [index: number]: string } = {
  0: 'bg-red-200', // Color for index 0
  1: 'bg-red-500', // Color for index 1
  2: 'bg-red-300', // Color for index 2
  3: 'bg-orange-200', // Color for index 3
  4: 'bg-orange-300', // Color for index 4
  5: 'bg-orange-400', // Color for index 5
  6: 'bg-orange-500', // Color for index 6
  7: 'bg-orange-600', // Color for index 7
  8: 'bg-amber-200', // Color for index 8
  9: 'bg-amber-300', // Color for index 9
  10: 'bg-amber-400', // Color for index 10
  11: 'bg-amber-500', // Color for index 11
  // Add more color mappings as needed
};

const keysToIgnore: Array<string> = [
  'Ctrl', 'Control', 'ContextMenu', 'Win', 'Enter', 'Alt', 'Space', 'Alt', 'Fn', 'Menu', 'Shift', 'CapsLock', 'Tab', '`', '-', '=', '[', ']',
  'Escape', 'Esc', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12', 'PrtScn', 'ScrLk', 'Pause',
  'Ins', 'Insert', 'Home', 'PgUp', 'PageUp', '{', '}', '|', 'Del', 'End', 'PgDn', 'PageDown',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Meta'
]; /* 'Backspace' & 'Delete' is not included in the list so that they
can be used for removing the inputs, but they cann't be added as
a valid key */

const reloadMessage: string = `
  <span class="bold">
    Navigating away might reset all the progress, Are you sure you want to proceed?
  </span>
`;

const onReloadConfirmationData: FuseConfirmationConfig = {
  title: `Leaving Current Page?`,
  message: reloadMessage,
  icon: {
    show: true,
    color: 'warn',
    name: 'heroicons_outline:exclamation',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Yes',
      color: 'primary',
    },
    cancel: {
      show: true,
      label: 'No',
    },
  },
  dismissible: false,
};

const duplicateHeadersCells: FuseConfirmationConfig = {
  title: `Duplicate header values!`,
  message: 'Selected header row contains duplicate values, please fix them and reupload or select a different header!',
  icon: {
    show: true,
    color: 'warn',
    name: 'heroicons_outline:exclamation',
  },
  actions: {
    cancel: {
      show: true,
      label: 'Close',
    },
    confirm: {
      show: false,
    },
  },
  dismissible: false,
};

const importConfirmationDialogConfig: FuseConfirmationConfig = {
  title: `Confirm Import`,
  message: `
    Are you ready to proceed with the import? This will update your user database according to the settings you've selected. Once started, this action cannot be undone.
    </br>
    </br>
    <span class="bold">Would you like to proceed?</span>
  `,
  icon: {
    show: true,
    color: 'success',
    name: 'heroicons_outline:information-circle',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Start import',
      color: 'primary',
    },
    cancel: {
      show: true,
      label: 'Back',
    },
  },
  dismissible: true,
}

export {
  STEPPER_SETTINGS,
  MAX_ROWS,
  MAX_COLUMNS,
  colorMapping,
  keysToIgnore,
  MAXIMUM_ROWS,
  SOCIUU_FIELDS,
  SOCIUU_FIELDS_KEYS,
  onReloadConfirmationData,
  duplicateHeadersCells,
  importConfirmationDialogConfig
};
